import { useMemo, useRef } from 'react'
import type { MutableRefObject, ReactNode } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { RoundedBox } from '@react-three/drei'
import { Merkplaat, Scanner, Waarschuwingsplaat, buisGeometrie, kapGeometrie } from './onderdelen'
import type { TruckKleuren } from './onderdelen'
import { Blok, DrivenOpschrift, PuWiel, Tussen, useMaticMaterialen } from './bouwdelen'
import type { Mat } from './bouwdelen'
import { useAnimatie } from '../../scene/store'
import { K_VORKMATEN } from './tempo'

/**
 * K-MATIC — autonome smalle-gangentruck (VNA), man-up, met draaikop.
 *
 * ## Waar de vorm vandaan komt
 *
 * Tien studiobeelden van Linde, in `docs/referenties/k-matic/`. Ze hebben een
 * echt alfakanaal, dus het silhouet is exact uit te lezen, net als bij de
 * T-Matic. Methode en alle getallen staan in `docs/kmatic-maten.md`.
 *
 * De machine is één chassis met drie delen erop, en dat is ook de opbouw hier:
 *
 *  - **aandrijfkop** (−z): de rode kap met vizier, grille en de Balyo-module
 *    ("Driven by Balyo") met scherm en 3D-lidar in een beugel;
 *  - **mast** (midden): een toren van 6 m ingetrokken, met open vensters in
 *    voor- en achterkant — je kijkt er dwars doorheen — en een rij
 *    trapeziumvormige uitsparingen in de zijwanden;
 *  - **cabine en draaikop** (+z): een platform met stoel, hekken en een
 *    lamellendak dat als geheel langs de mast omhoog gaat, met voorop de
 *    draaikop: een dwarsslede met een kolom waaraan de vorken dwars naar de
 *    stelling wijzen.
 *
 * ## Typeblad
 *
 * De vorm is gemeten op de beelden met de mast als ankermaat; de maten die op
 * het VDI-typeblad (serie 5231) staan, komen daarvandaan: h1 6400 (de mast
 * van 14,35 m heffing, die op de beelden staat), h2 5150, h6 2555, h610 2300,
 * h13 60, vork 50 × 120 × 1200, b1 1450, b2 1750, B 1540. Zie
 * `docs/kmatic-maten.md`.
 */
const SCHAAL = 1

/** Hoogte van de ingetrokken mast, h1. */
const MAST_H = 6.4
/**
 * De mastdetails zijn op de beelden gemeten met de mast op 6,0 m; met de
 * echte h1 schalen ze mee.
 */
const M = MAST_H / 6.0
/** Bovenkant chassis onder mast en cabine; onder de kap ligt het lager. */
const CHASSIS_H = 0.43
const PLAAT_H = 0.24
/** Mast in het chassis: z van cabinezijde naar aandrijfzijde, en halve breedte. */
const MAST_Z0 = 0.22
const MAST_Z1 = -0.52
const MAST_HB = 0.535
/** Bovenkant van de rode kap. */
const KAP_H = 1.6

/**
 * Vork gezakt: de bovenkant op h13 = 60 mm, het blad 50 mm dik.
 */
const VORK_Y = 0.035
/**
 * Tot waar de cabine gaat zonder dat de mast uitschuift (vrije heffing h2).
 * In de hal werkt hij op het eerste en tweede stellingniveau, ruim eronder.
 */
const VRIJE_HEF = 5.15
/**
 * Hefstand zonder werkcyclus (de showroom): het tweede stellingniveau. De
 * pallet staat daar op 3,82 m (`Lading`), zijn vorkopeningen lopen tot 12 cm
 * boven zijn onderkant, en de ligger eronder komt tot 3,87 m. Een blad van
 * 50 mm met zijn midden op 3,90 m past daar tussen.
 */
const HEF_HOOGTE = 3.9 - VORK_Y
/**
 * Midden tussen de twee vorken, langs de lengteas. `Magazijn` lijnt de
 * werkgang daarop uit, zodat de vorken recht voor een pallet uitkomen.
 *
 * Zo ver voor de dwarsslede (voorkant 1,41) dat een pallet erop — 1200 in de
 * lengte van de truck — er met 4 cm vrij van blijft.
 */
export const VORK_MIDDEN = 2.05
/**
 * Hoe ver de dwarsslede de vorken de stelling in schuift: de kolom loopt over
 * de hele slede (B = 1540), van −0,58 tot +0,77. Dan staat de vorkhiel op de
 * stellingrand en de pallet midden in het vak.
 */
const SCHUIF = K_VORKMATEN.schuif

/** Wat de werkcyclus (`Matic`) van de vorken moet weten. */
export const K_VORK = { rust: VORK_Y, blad: K_VORKMATEN.blad, midden: VORK_MIDDEN, schuif: SCHUIF }

/**
 * Dwarsbalken in voor- en achterkant van de buitenmast, uit het vooraanzicht,
 * en de dichte onderkant tot 2,75 m. Wat ertussen openblijft zijn de drie
 * vensters (2,75–3,37, 3,58–4,45 en 4,66–5,74 m), waar je dwars doorheen kijkt.
 */
const BALKEN: [number, number][] = [[5.74 * M, MAST_H], [4.45 * M, 4.66 * M], [3.37 * M, 3.58 * M], [CHASSIS_H, 2.75 * M]]
/** Uitsparingen in de zijwanden: zeven stuks, om de 51 cm. */
const UITSPARINGEN = Array.from({ length: 7 }, (_, i) => (2.09 + i * 0.51) * M)

/* ------------------------------------------------------------------ chassis */

function Chassis({ m }: { m: Mat }) {
  return (
    <group>
      {/* Bodemplaat over de hele lengte: onder de kap steekt hij als een
          richel uit, want het chassis is breder dan de kap. */}
      <RoundedBox args={[1.44, PLAAT_H - 0.06, 2.62]} position={[0, (PLAAT_H + 0.06) / 2, -0.31]}
        radius={0.05} smoothness={3} material={m.chassis} castShadow receiveShadow />
      {/* Verhoogd deel onder mast en cabine. */}
      <Tussen a={[-0.72, PLAAT_H - 0.02, -0.2]} b={[0.72, CHASSIS_H, 1.0]} m={m.chassis} />
      {/* Aan de lastzijde twee poten met de lastwielen; daartussen zakt de
          dwarsslede van de draaikop tot vlak boven de vloer, op een lage
          plaat die je pas ziet als de cabine omhoog is. */}
      <Tussen a={[-0.61, 0.06, 1.0]} b={[0.61, 0.16, 1.5]} m={m.chassis} />
      {[-1, 1].map((k) => (
        <group key={k}>
          <Tussen a={[k * 0.61, 0.08, 0.9]} b={[k * 0.74, CHASSIS_H, 1.54]} m={m.chassis} />
          <mesh position={[k * 0.745, 0.26, 1.2]} rotation={[0, k * Math.PI / 2, 0]} material={m.zwart}>
            <circleGeometry args={[0.022, 14]} />
          </mesh>
          {/* Gele markering op de hoeken. Geen arcering: die staat op de
              draaitafel van Linde nergens. */}
          <mesh position={[k * 0.56, 0.16, -1.626]} rotation={[0, Math.PI, 0]} material={m.geel}>
            <planeGeometry args={[0.035, 0.035]} />
          </mesh>
          <mesh position={[k * 0.675, 0.3, 1.542]} material={m.geel}>
            <planeGeometry args={[0.035, 0.035]} />
          </mesh>
        </group>
      ))}

      {/* Wielen: aandrijfwiel onder de kap, lastwielen in de poten en
          steunwielen op de hoeken van de aandrijfzijde. Allemaal PU. */}
      <PuWiel p={[0, 0.17, -0.95]} r={0.17} b={0.13} m={m} />
      {[-1, 1].map((k) => <PuWiel key={`l${k}`} p={[k * 0.675, 0.12, 1.3]} r={0.12} b={0.1} m={m} />)}
      {[-1, 1].map((k) => <PuWiel key={`z${k}`} p={[k * 0.55, 0.07, -1.38]} r={0.07} b={0.08} m={m} />)}
      {/* Geleiderollen op de hoeken: een smalle-gangentruck rijdt langs een
          rail. Over de rollen gemeten is hij b2 = 1750 breed. */}
      {[[-1, 1.44], [1, 1.44], [-1, -1.5], [1, -1.5]].map(([k, z]) => (
        <group key={`g${k}${z}`}>
          <Tussen a={[k * 0.72, 0.04, z - 0.05]} b={[k * 0.8, 0.12, z + 0.05]} m={m.chassis} />
          <PuWiel p={[k * 0.825, 0.08, z]} r={0.05} b={0.05} m={m} staand={false} />
        </group>
      ))}

      {/* Veiligheidsscanner laag in de neus en op beide poten. */}
      <group position={[0, 0.15, -1.63]} rotation={[0, Math.PI, 0]}><Scanner schaal={1.6} /></group>
      {[-1, 1].map((k) => (
        <group key={k} position={[k * 0.675, 0.15, 1.56]}><Scanner schaal={1.1} /></group>
      ))}
    </group>
  )
}

/* ------------------------------------------------------------------ aandrijfkop */

/**
 * Het grijze zwaaivlak op de zijkant van de kap: een punt die naar voren
 * wijst, onderaan achter breed. In (−z, y), dus positief naar de neus.
 */
function zwaaiVorm() {
  const s = new THREE.Shape()
  s.moveTo(0.27, 0.84)
  s.lineTo(0.87, 0.82)
  s.quadraticCurveTo(0.62, 0.6, 0.42, 0.42)
  s.quadraticCurveTo(0.34, 0.33, 0.27, 0.28)
  s.closePath()
  return new THREE.ShapeGeometry(s, 12)
}

/**
 * De kap: breed onderaan, iets smaller boven, en een bovenkant die 8 cm
 * afloopt naar de neus — op het zijaanzicht is dat het verschil tussen een
 * kist en een motorkap. Ribben met een straal van 26 cm: op de draaitafel is
 * de neus rond, geen kist met afgeronde hoeken.
 */
function kapVorm() {
  const h = KAP_H - PLAAT_H
  const d = 1.32
  const g = kapGeometrie(1.16, h, d, 0.26, 0.93, 0.94)
  const pos = g.attributes.position as THREE.BufferAttribute
  for (let i = 0; i < pos.count; i++) {
    const hoog = (pos.getY(i) + h / 2) / h
    const voor = (d / 2 - pos.getZ(i)) / d
    pos.setY(i, pos.getY(i) - hoog * voor * voor * 0.08)
  }
  pos.needsUpdate = true
  g.computeVertexNormals()
  return g
}

/**
 * Het vizier: een donkere glasband die om de ronde neus heen loopt, op de
 * draaitafel van Linde van 1,16 tot 1,40 m en tot ver op de zijkant. Een plat
 * paneel ervoor leest als een sticker; daarom is het de kap zelf, 5 mm groter,
 * en met drie clipvlakken bijgesneden tot de band. Zo volgt het elke ronding
 * van de kap exact.
 */
function useVizier(m: Mat) {
  const gl = useThree((st) => st.gl)
  return useMemo(() => {
    gl.localClippingEnabled = true
    const mat = (m.glas as THREE.MeshPhysicalMaterial).clone()
    mat.transparent = false
    mat.opacity = 1
    mat.clippingPlanes = [
      new THREE.Plane(new THREE.Vector3(0, 1, 0), -1.16),
      new THREE.Plane(new THREE.Vector3(0, -1, 0), 1.4),
      // Tot 0,3 m achter de neus: op de zijkant loopt hij door tot boven het
      // voorste deel van het zwaaivlak.
      new THREE.Plane(new THREE.Vector3(0, 0, -1), -1.14),
    ]
    return mat
  }, [gl, m.glas])
}

/**
 * Clipvlakken staan in wereldcoördinaten; het vizier rijdt mee met de truck.
 * Per frame zetten we ze vanuit het stelsel van `anker` om.
 */
function useMeebewegendeClip(mesh: MutableRefObject<THREE.Object3D | null>, mat: THREE.Material) {
  const lokaal = useMemo(() => (mat.clippingPlanes ?? []).map((p) => p.clone()), [mat])
  useFrame(() => {
    const o = mesh.current
    if (!o || !mat.clippingPlanes) return
    o.updateWorldMatrix(true, false)
    mat.clippingPlanes.forEach((p, i) => p.copy(lokaal[i]).applyMatrix4(o.matrixWorld))
  })
}

function Aandrijfkop({ m }: { m: Mat }) {
  const kap = useMemo(() => kapVorm(), [])
  const vizier = useVizier(m)
  const anker = useRef<THREE.Group>(null)
  useMeebewegendeClip(anker, vizier)
  const zwaai = useMemo(() => zwaaiVorm(), [])
  const beugel = useMemo(
    () => buisGeometrie([[-0.22, 0, 0], [-0.24, 0.3, 0], [-0.14, 0.52, 0], [0, 0.57, 0],
      [0.14, 0.52, 0], [0.24, 0.3, 0], [0.22, 0, 0]], 0.022, 36),
    [],
  )

  return (
    <group>
      <mesh geometry={kap} position={[0, (KAP_H + PLAAT_H) / 2, -0.86]} material={m.lakRood} castShadow receiveShadow />

      {[-1, 1].map((k) => (
        <group key={k}>
          {/* Zwaaivlak met embleem. Rechts (+x) draait de vorm een kwartslag
              zodat zijn x naar −z wijst; links staat hij in spiegelbeeld,
              anders wees de punt daar naar achteren. */}
          <mesh geometry={zwaai} position={[k * 0.583, 0, 0]} rotation={[0, k * Math.PI / 2, 0]}
            scale={[k, 1, 1]} material={m.zwaai} />
          <Merkplaat positie={[k * 0.586, 0.66, -0.4]} rotatie={[0, k * Math.PI / 2, 0]} breedte={0.2} type="" />
          {/* Twee ventilatiesleuven bovenin. */}
          {[-0.36, -0.79].map((z) => (
            <Blok key={z} p={[k * 0.57, 1.07, z]} s={[0.006, 0.022, 0.1]} m={m.zwart} schaduw={false} />
          ))}
          {/* Het vizier loopt om de hoek door op de zijkant. */}
          <Tussen a={[k * 0.556, 1.16, -1.36]} b={[k * 0.562, 1.38, -1.14]} m={m.glas} schaduw={false} />
        </group>
      ))}

      {/* Vizier: de kap zelf, iets groter, bijgesneden tot de band om de neus
          (zie useVizier). De clipvlakken rekenen vanuit het stelsel van de
          truck: het lege anker hieronder staat op zijn oorsprong. */}
      <group ref={anker} />
      <mesh geometry={kap} scale={1.006} position={[0, (KAP_H + PLAAT_H) / 2, -0.86]} material={vizier} />
      <Merkplaat positie={[0, 1.33, -1.515]} rotatie={[0, Math.PI, 0]} breedte={0.24} type="" beugel={false} />
      {/* Grille rechtsonder in de neus (vanaf voren gezien): acht sleuven. */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <Blok key={i} p={[-0.2, 0.62 + i * 0.026, -1.508]} s={[0.22, 0.011, 0.012]} m={m.zwart} schaduw={false} />
      ))}
      {/* Ventilatiesleuven op de voorhoek, en de sleuf van de voorste scanner. */}
      {[-1, 1].map((k) => [0, 1, 2, 3, 4].map((i) => (
        <Blok key={`${k}${i}`} p={[k * 0.54, 0.86 + i * 0.024, -1.4]} s={[0.012, 0.01, 0.08]} m={m.zwart} schaduw={false} />
      )))}
      <Tussen a={[-0.26, 0.38, -1.52]} b={[0.26, 0.42, -1.49]} m={m.zwart} schaduw={false} />
      {/* Donkere onderrand van de neus, waar hij op de bodemplaat rust. */}
      <Tussen a={[-0.52, PLAAT_H, -1.525]} b={[0.52, PLAAT_H + 0.1, -1.49]} m={m.kunststof} schaduw={false} />

      {/* Mastvoet: donker huis aan de cabinezijde van de kap, met etiket en
          drukknop — op het zijaanzicht de verticale band tussen kap en mast. */}
      <Tussen a={[-0.585, CHASSIS_H, -0.2]} b={[0.585, 1.52, 0.06]} m={m.chassis} />
      {[-1, 1].map((k) => (
        <group key={k}>
          <mesh position={[k * 0.587, 1.4, -0.02]} rotation={[0, k * Math.PI / 2, 0]} material={m.geel}>
            <planeGeometry args={[0.06, 0.07]} />
          </mesh>
          <mesh position={[k * 0.59, 1.4, -0.14]} rotation={[0, 0, Math.PI / 2]} material={m.zwart}>
            <cylinderGeometry args={[0.024, 0.024, 0.012, 14]} />
          </mesh>
        </group>
      ))}

      {/* Balyo-module op de neus: behuizing met scherm en woordmerk, beugel
          met 3D-lidar, noodstop op de zijkant. */}
      <group position={[0, KAP_H - 0.045, -1.07]}>
        <RoundedBox args={[0.66, 0.26, 0.2]} position={[0, 0.13, 0]} radius={0.02} smoothness={2}
          material={m.kunststof} castShadow />
        <mesh position={[0.18, 0.14, -0.102]} rotation={[0, Math.PI, 0]} material={m.scherm}>
          <planeGeometry args={[0.16, 0.1]} />
        </mesh>
        <DrivenOpschrift positie={[-0.1, 0.14, -0.102]} />
        {[-1, 1].map((k) => (
          <group key={k}>
            {/* Noodstop: geel vlak, rode knop. */}
            <mesh position={[k * 0.332, 0.12, -0.02]} rotation={[0, 0, Math.PI / 2]} material={m.geel}>
              <cylinderGeometry args={[0.045, 0.045, 0.01, 18]} />
            </mesh>
            <mesh position={[k * 0.345, 0.12, -0.02]} rotation={[0, 0, Math.PI / 2]} material={m.noodstop}>
              <cylinderGeometry args={[0.022, 0.026, 0.025, 14]} />
            </mesh>
            {/* Markeringslampje op de hoek */}
            <mesh position={[k * 0.31, 0.22, -0.1]}>
              <boxGeometry args={[0.03, 0.03, 0.02]} />
              <meshStandardMaterial color="#e8791a" emissive="#e8791a" emissiveIntensity={0.3} />
            </mesh>
          </group>
        ))}
        <mesh geometry={beugel} position={[0, 0.26, 0]} material={m.kunststof} castShadow />
        {/* De lidar hangt ín de beugel, met de blauwe lens naar voren: zijn
            scanvlak ligt op h610 = 2300. */}
        <group position={[0, 2.3 - (KAP_H - 0.045), -0.01]}>
          <RoundedBox args={[0.13, 0.15, 0.12]} radius={0.02} smoothness={2} material={m.zwart} castShadow />
          <mesh position={[0, 0, -0.063]} rotation={[Math.PI / 2, 0, 0]} material={m.lens}>
            <cylinderGeometry args={[0.03, 0.03, 0.01, 16]} />
          </mesh>
        </group>
      </group>
    </group>
  )
}

/* ------------------------------------------------------------------ mast */

/**
 * Eén vlak (voor of achter) van de buitenmast: twee brede flenzen met
 * dwarsbalken ertussen. De vensters ertussen zijn echt open.
 */
function MastVlak({ z, m }: { z: number; m: Mat }) {
  const flens = MAST_HB - 0.26
  const h = MAST_H - CHASSIS_H
  return (
    <group>
      {[-1, 1].map((k) => (
        <group key={k}>
          <Blok p={[k * (MAST_HB - flens / 2), CHASSIS_H + h / 2, z]} s={[flens, h, 0.05]} m={m.mast} />
          {/* Een verdiepte naad in de flens: zo leest hij als profiel, niet als plaat. */}
          <Blok p={[k * (MAST_HB - 0.07), CHASSIS_H + h / 2, z + Math.sign(z) * 0.026]}
            s={[0.012, h - 0.1, 0.004]} m={m.chassis} schaduw={false} />
        </group>
      ))}
      {BALKEN.map(([a, b]) => (
        <Blok key={a} p={[0, (a + b) / 2, z]} s={[0.52, b - a, 0.05]} m={m.mast} />
      ))}
    </group>
  )
}

/**
 * Zijwand van de mast als plaat met gaten: door de uitsparingen zie je de
 * binnenmast, en aan de aandrijfkant — waar geen binnenmast meer achter zit —
 * de achtergrond. Dat laatste is op de fabrieksbeelden het duidelijkst.
 */
function zijwandGeometrie() {
  const s = new THREE.Shape()
  // Bovenhoek aan de aandrijfkant afgerond (straal 120 mm), zoals op de
  // draaitafel; daar komt de slanggeleider uit.
  const r = 0.12
  s.moveTo(MAST_Z1, CHASSIS_H)
  s.lineTo(MAST_Z0, CHASSIS_H)
  s.lineTo(MAST_Z0, MAST_H)
  s.lineTo(MAST_Z1 + r, MAST_H)
  s.quadraticCurveTo(MAST_Z1, MAST_H, MAST_Z1, MAST_H - r)
  s.closePath()
  for (const y of UITSPARINGEN) {
    const gat = new THREE.Path()
    gat.moveTo(-0.46, y - 0.1)
    gat.lineTo(-0.085, y - 0.05)
    gat.lineTo(-0.085, y + 0.05)
    gat.lineTo(-0.46, y + 0.1)
    gat.closePath()
    s.holes.push(gat)
  }
  return new THREE.ExtrudeGeometry(s, { depth: 0.04, bevelEnabled: false })
}

function Mast({ m, binnen }: { m: Mat; binnen: MutableRefObject<THREE.Group | null> }) {
  const h = MAST_H - CHASSIS_H
  const zijwand = useMemo(() => zijwandGeometrie(), [])

  return (
    <group>
      {/* Zijwanden. De vorm staat in (z, y); een kwartslag om y zet zijn x op
          de z-as en de extrusie op −x, dus hij ligt binnen de buitenmaat. */}
      {[-1, 1].map((k) => (
        <group key={k}>
          <mesh geometry={zijwand} material={m.mast} castShadow receiveShadow
            position={[k === 1 ? MAST_HB : -MAST_HB + 0.04, 0, 0]} rotation={[0, -Math.PI / 2, 0]} />
          <Waarschuwingsplaat positie={[k * (MAST_HB + 0.002), 1.72, 0.14]} rotatie={[0, k * Math.PI / 2, 0]} />
          <Merkplaat positie={[k * (MAST_HB + 0.003), MAST_H - 0.3, -0.1]} rotatie={[0, k * Math.PI / 2, 0]}
            breedte={0.3} type="" />
        </group>
      ))}
      <MastVlak z={MAST_Z0 - 0.025} m={m} />
      <MastVlak z={MAST_Z1 + 0.025} m={m} />
      {/* Kop: open, zoals op de draaitafel — de profielen lopen door tot
          boven, zonder dekplaat. Alleen de beugels van de slanggeleiders. */}
      {[-1, 1].map((k) => (
        <Tussen key={k} a={[k * 0.44, MAST_H - 0.16, MAST_Z1 - 0.07]} b={[k * 0.52, MAST_H - 0.02, MAST_Z1]} m={m.chassis} />
      ))}

      {/* Binnenmast: profielen achter de flenzen, de hefcilinder in het
          midden — de middenstijl in elk venster — en de kettingen ernaast.
          Schuift uit als de cabine boven de vrije heffing komt. */}
      <group ref={binnen}>
        {[-1, 1].map((k) => (
          <group key={k}>
            <Tussen a={[k * 0.27, CHASSIS_H + 0.1, -0.35]} b={[k * 0.33, MAST_H - 0.04, 0.15]} m={m.binnen} />
            <Tussen a={[k * 0.045, CHASSIS_H + 0.2, 0.08]} b={[k * 0.065, MAST_H - 0.2, 0.1]} m={m.zwart} schaduw={false} />
          </group>
        ))}
        <mesh position={[0, CHASSIS_H + 0.1 + (h - 0.3) / 2, -0.1]} material={m.chassis} castShadow>
          <cylinderGeometry args={[0.045, 0.045, h - 0.3, 14]} />
        </mesh>
        <Tussen a={[-0.33, MAST_H - 0.1, -0.35]} b={[0.33, MAST_H - 0.04, 0.15]} m={m.chassis} />
      </group>
    </group>
  )
}

/** De twee lange slanggeleiders van de mastkop naar de Balyo-module. */
function Slanggeleiders({ m }: { m: Mat }) {
  const staven = useMemo(() => [-1, 1].map((k) => {
    const boven = new THREE.Vector3(k * 0.48, MAST_H - 0.09, MAST_Z1 - 0.04)
    const onder = new THREE.Vector3(k * 0.3, KAP_H + 0.18, -1.07)
    const lengte = boven.distanceTo(onder)
    // Ø 60 mm: op de draaitafel zijn het forse, ronde slangen, geen staafjes.
    const g = new THREE.CylinderGeometry(0.03, 0.03, lengte, 12)
    // Cilinder langs +y vanaf de onderkant, dan +y naar de richting draaien.
    g.translate(0, lengte / 2, 0)
    const q = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0), boven.clone().sub(onder).normalize(),
    )
    return { g, onder, q }
  }), [])
  return (
    <>
      {staven.map(({ g, onder, q }, i) => (
        <mesh key={i} geometry={g} material={m.kunststof} castShadow position={onder} quaternion={q} />
      ))}
    </>
  )
}

/* ------------------------------------------------------------------ cabine */

function Cabine({ m }: { m: Mat }) {
  const vloer = CHASSIS_H + 0.06
  // Bovenkant beschermdak h6 = 2555.
  const dak = 2.525
  const greep = useMemo(
    () => buisGeometrie([[0, 0, -0.3], [0, 0.14, -0.1], [0.04, 0.2, 0.15], [0.12, 0.16, 0.38]], 0.02, 24),
    [],
  )
  return (
    <group>
      {/* Vloer */}
      <Tussen a={[-0.62, CHASSIS_H, 0.24]} b={[0.62, vloer, 1.1]} m={m.chassis} />

      {/* Achterwand tegen de mast: twee stijlen tot het dak, een paneel en
          daarboven de ruit waardoor de bestuurder de mast ziet. */}
      {/* Op de draaitafel is de achterwand grijs bekleed, met een hoog raam
          met ronde bovenhoeken: geen zwart vlak. */}
      {[-1, 1].map((k) => (
        <Tussen key={k} a={[k * 0.43, vloer, 0.24]} b={[k * 0.51, dak, 0.32]} m={m.zwaai} />
      ))}
      <Tussen a={[-0.43, vloer, 0.25]} b={[0.43, 1.5, 0.29]} m={m.zwaai} />
      <Tussen a={[-0.43, 1.5, 0.25]} b={[-0.12, 2.38, 0.29]} m={m.zwaai} />
      <Tussen a={[0.12, 1.5, 0.25]} b={[0.43, 2.38, 0.29]} m={m.zwaai} />
      <RoundedBox args={[0.24, 0.84, 0.012]} position={[0, 1.94, 0.27]} radius={0.005} smoothness={2} material={m.glas} />
      <Tussen a={[-0.43, 2.38, 0.25]} b={[0.43, dak, 0.3]} m={m.zwaai} />

      {/* Stoel: voet, zitting, rugleuning. De bestuurder kijkt naar de vorken. */}
      <Tussen a={[-0.12, vloer, 0.46]} b={[0.12, 0.86, 0.68]} m={m.chassis} />
      <RoundedBox args={[0.5, 0.11, 0.46]} position={[0, 0.91, 0.6]} radius={0.04} smoothness={2}
        material={m.zwart} castShadow />
      <RoundedBox args={[0.5, 0.66, 0.12]} position={[0, 1.3, 0.4]} rotation={[-0.1, 0, 0]}
        radius={0.05} smoothness={2} material={m.zwart} castShadow />

      {/* Zijhekken: een raam van profiel met spijlen in de onderste helft,
          en daarop de bedieningsconsole met de gebogen greep. */}
      {[-1, 1].map((k) => (
        <group key={k}>
          {/* Open zijframe van plaat: brede voorstijl, smalle achterstijl, een
              bovenregel en onderin een plint — geen spijlen. */}
          <Tussen a={[k * 0.6, vloer, 0.32]} b={[k * 0.64, 1.5, 0.38]} m={m.mast} />
          <Tussen a={[k * 0.6, vloer, 0.96]} b={[k * 0.64, 1.5, 1.08]} m={m.mast} />
          <Tussen a={[k * 0.6, 1.44, 0.32]} b={[k * 0.64, 1.5, 1.08]} m={m.mast} />
          <Tussen a={[k * 0.6, vloer, 0.32]} b={[k * 0.64, vloer + 0.16, 1.08]} m={m.mast} />
          {/* Console met joystick, en een zwarte armleuning erop. */}
          <Tussen a={[k * 0.44, 1.12, 0.52]} b={[k * 0.6, 1.2, 1.02]} m={m.kunststof} />
          <RoundedBox args={[0.1, 0.06, 0.4]} position={[k * 0.52, 1.23, 0.62]} radius={0.025} smoothness={3}
            material={m.zwart} castShadow />
          <mesh position={[k * 0.52, 1.25, 0.88]} material={m.zwart}>
            <cylinderGeometry args={[0.018, 0.022, 0.1, 10]} />
          </mesh>
          {/* Gebogen greep naar voren en naar buiten, met gele knop en noodstop. */}
          <group position={[k * 0.62, 1.5, 0.7]} scale={[k, 1, 1]}>
            <mesh geometry={greep} material={m.kunststof} castShadow />
            <mesh position={[0.12, 0.16, 0.41]} material={m.geel}>
              <boxGeometry args={[0.05, 0.05, 0.07]} />
            </mesh>
            <mesh position={[0.12, 0.2, 0.41]} material={m.noodstop}>
              <cylinderGeometry args={[0.016, 0.018, 0.02, 12]} />
            </mesh>
          </group>
        </group>
      ))}

      {/* Bedieningsbalk over de voorkant, licht schuin, met gele grepen en
          noodstoppen op de uiteinden (draaitafel, frame 0 en 27). */}
      <group position={[0, 1.42, 1.1]} rotation={[0.25, 0, 0]}>
        <Tussen a={[-0.66, -0.03, -0.03]} b={[0.66, 0.03, 0.03]} m={m.mast} />
        {[-1, 1].map((k) => (
          <group key={k} position={[k * 0.66, 0, 0]}>
            <Tussen a={[-0.04, -0.03, -0.035]} b={[0.04, 0.035, 0.035]} m={m.geel} />
            <mesh position={[0, 0.05, 0]} material={m.noodstop}>
              <cylinderGeometry args={[0.014, 0.016, 0.022, 12]} />
            </mesh>
          </group>
        ))}
      </group>

      {/* Lamellendak, vrijdragend vanaf de achterwand, met een omgezette rand. */}
      <Tussen a={[-0.53, dak, 0.22]} b={[0.53, dak + 0.03, 0.3]} m={m.kunststof} />
      <Tussen a={[-0.53, dak - 0.08, 1.13]} b={[0.53, dak + 0.03, 1.17]} m={m.kunststof} />
      {[-1, 1].map((k) => (
        <Tussen key={k} a={[k * 0.5, dak - 0.05, 0.22]} b={[k * 0.53, dak + 0.03, 1.17]} m={m.kunststof} />
      ))}
      {Array.from({ length: 11 }, (_, i) => -0.45 + i * 0.09).map((x) => (
        <Tussen key={x} a={[x - 0.018, dak - 0.02, 0.3]} b={[x + 0.018, dak + 0.02, 1.13]} m={m.kunststof} />
      ))}
    </group>
  )
}

/* ------------------------------------------------------------------ draaikop */

/** Bovenkant van de dwarsslede, gezakt: net boven de zitting, niet tot de console. */
const SLEDE_TOP = 0.9

/** Tanden van een heugel: donkere en lichtere strepen, herhaald over de lengte. */
let tanden: THREE.CanvasTexture | null = null
function tandTextuur() {
  if (tanden) return tanden
  const c = document.createElement('canvas')
  c.width = 32
  c.height = 8
  const g = c.getContext('2d')
  if (!g) return null
  g.fillStyle = '#0d0e10'
  g.fillRect(0, 0, 32, 8)
  g.fillStyle = '#3a3c41'
  for (let x = 0; x < 32; x += 8) g.fillRect(x + 1, 0, 4, 8)
  tanden = new THREE.CanvasTexture(c)
  tanden.colorSpace = THREE.SRGBColorSpace
  tanden.wrapS = THREE.RepeatWrapping
  return tanden
}

/** Een zwarte tandheugel over de voorkant van de slede: 60 mm hoog, 25 mm diep. */
function Tandheugel({ x0, x1, y, z }: { x0: number; x1: number; y: number; z: number }) {
  const mat = useMemo(() => {
    const t = tandTextuur()?.clone()
    if (t) {
      t.repeat.set((x1 - x0) / 0.02, 1)
      t.needsUpdate = true
    }
    return new THREE.MeshStandardMaterial({ map: t ?? null, color: '#ffffff', roughness: 0.5, metalness: 0.3 })
  }, [x0, x1])
  return (
    <mesh position={[(x0 + x1) / 2, y, z + 0.0125]} material={mat} castShadow>
      <boxGeometry args={[x1 - x0, 0.06, 0.025]} />
    </mesh>
  )
}

/**
 * Draaikop: een dwarsslede over de volle breedte met geleiderails, een kolom
 * met motorkast die over die rails loopt, en de vorken dwars aan de kolom.
 *
 * `schuif` is een ref zodat de animatie hem per frame zet zonder rerender.
 * Hij staat in een groep die met `kant` gespiegeld wordt: de vorken wijzen
 * naar de stelling aan de ene of de andere kant van het gangpad.
 */
function Draaikop({ m, schuif, lading }: {
  m: Mat
  schuif: MutableRefObject<THREE.Group | null>
  lading?: ReactNode
}) {
  const kolomX = -0.58
  return (
    <group>
      {/* Dwarsslede (B = 1540) met vier geleiderails op de voorkant. Tussen
          de poten zakt hij tot vlak boven de vloer, erbuiten rust hij erop. */}
      <Tussen a={[-0.6, 0.22, 1.1]} b={[0.6, 0.46, 1.38]} m={m.chassis} />
      <Tussen a={[-0.77, 0.46, 1.1]} b={[0.77, SLEDE_TOP, 1.38]} m={m.chassis} />
      {/* Twee zwarte tandheugels over de voorkant, boven en onder: daar loopt
          de kolom over (draaitafel, frame 0 en 27). Tussenin een geleiderail. */}
      <Tandheugel x0={-0.77} x1={0.77} y={SLEDE_TOP - 0.04} z={1.38} />
      <Tandheugel x0={-0.6} x1={0.6} y={0.3} z={1.38} />
      <Tussen a={[-0.77, 0.58, 1.38]} b={[0.77, 0.62, 1.405]} m={m.mast} />
      {/* Console op de slede, met het scherm naar de bestuurder. */}
      <Tussen a={[-0.36, SLEDE_TOP, 1.0]} b={[0.36, SLEDE_TOP + 0.24, 1.14]} m={m.kunststof} />
      <mesh position={[0, SLEDE_TOP + 0.14, 0.998]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[0.22, 0.13]} />
        <meshStandardMaterial color="#0d1b24" emissive="#1c4a66" emissiveIntensity={0.35} roughness={0.2} />
      </mesh>

      <group ref={schuif}>
        {/* Kolom: twee smalle profielen met een kap en een cilinder erop. */}
        {[-1, 1].map((k) => (
          <Tussen key={k} a={[kolomX + k * 0.095 - 0.035, 0.2, 1.42]} b={[kolomX + k * 0.095 + 0.035, 2.86, 1.66]} m={m.mast} />
        ))}
        <Tussen a={[kolomX - 0.06, 0.2, 1.46]} b={[kolomX + 0.06, 2.8, 1.62]} m={m.chassis} />
        <Tussen a={[kolomX - 0.15, 2.83, 1.42]} b={[kolomX + 0.15, 2.88, 1.66]} m={m.chassis} />
        <mesh position={[kolomX, 2.95, 1.54]} material={m.chassis} castShadow>
          <cylinderGeometry args={[0.035, 0.035, 0.14, 12]} />
        </mesh>
        {/* Rode motorkast op de slede, achter de kolom, en de rode
            zwenkaandrijving op de buitenkant. */}
        <RoundedBox args={[0.26, 0.34, 0.3]} position={[kolomX + 0.02, SLEDE_TOP + 0.17, 1.24]} radius={0.03} smoothness={2}
          material={m.lakRood} castShadow />
        <RoundedBox args={[0.08, 0.5, 0.22]} position={[kolomX - 0.17, 0.62, 1.54]} radius={0.02} smoothness={2}
          material={m.lakRood} castShadow />

        {/* Vorkenbord aan de binnenkant van de kolom, met een rood paneel.
            De vorken liggen vóór de poten van het chassis: gezakt schuiven ze
            er net overheen. */}
        <Tussen a={[kolomX + 0.13, 0.18, 1.46]} b={[kolomX + 0.15, 0.64, 1.62]} m={m.chassis} />
        <Tussen a={[kolomX + 0.15, 0.1, 1.54]} b={[kolomX + 0.19, 0.72, VORK_MIDDEN + 0.28]} m={m.mast} />
        <mesh position={[kolomX + 0.17, 0.46, VORK_MIDDEN + 0.286]} material={m.lakRood}>
          <boxGeometry args={[0.07, 0.3, 0.012]} />
        </mesh>
        {[VORK_MIDDEN - 0.2, VORK_MIDDEN + 0.2].map((z) => (
          <group key={z}>
            {/* Vorkhiel en blad, dwars naar de stelling: 50 × 120 × 1200. */}
            <Tussen a={[kolomX + 0.19, VORK_Y - 0.025, z - 0.06]} b={[kolomX + 0.24, 0.56, z + 0.06]} m={m.vork} />
            <Tussen a={[kolomX + 0.19, VORK_Y - 0.025, z - 0.06]} b={[kolomX + 1.35, VORK_Y + 0.025, z + 0.06]} m={m.vork} />
            <Tussen a={[kolomX + 1.35, VORK_Y - 0.025, z - 0.06]} b={[kolomX + 1.39, VORK_Y, z + 0.06]} m={m.vork} />
          </group>
        ))}
        {lading}
      </group>
    </group>
  )
}

/* ------------------------------------------------------------------ de machine */

export function KMatic({ kleuren, hef, kant, stand, lading }: {
  kleuren: TruckKleuren
  hef: MutableRefObject<number>
  /** Kant waar de vorken naartoe wijzen: +1 is +x lokaal, −1 is −x. */
  kant?: MutableRefObject<number>
  /**
   * Vorkstand van de werkcyclus: hoogte van het blad boven de vloer en hoe
   * ver de vorken uit zijn (0..1). Gaat vóór `hef`.
   */
  stand?: MutableRefObject<{ hoogte: number; uit: number }>
  /** Wat er op de vorken ligt, in het assenstelsel van de dwarsslede. */
  lading?: ReactNode
}) {
  const m = useMaticMaterialen(kleuren)
  const anim = useAnimatie()
  const hefGroep = useRef<THREE.Group>(null)
  const binnen = useRef<THREE.Group | null>(null)
  const spiegel = useRef<THREE.Group>(null)
  const schuif = useRef<THREE.Group | null>(null)

  useFrame(() => {
    const h = anim.aan && anim.mast ? hef.current : 0
    // Eerst gaat de cabine naar het stellingniveau, dan schuiven de vorken
    // de stelling in — en bij het zakken omgekeerd. Met `hef` als één curve
    // 0→1→0 volgt die volgorde vanzelf uit de twee drempels.
    const s = stand?.current
    const hoog = s ? Math.max(0, s.hoogte - VORK_Y) : THREE.MathUtils.smoothstep(h, 0, 0.7) * HEF_HOOGTE
    const uit = (s ? s.uit : THREE.MathUtils.smoothstep(h, 0.7, 1)) * SCHUIF
    if (hefGroep.current) hefGroep.current.position.y = hoog
    if (binnen.current) binnen.current.position.y = Math.max(0, hoog - VRIJE_HEF)
    if (schuif.current) schuif.current.position.x = uit
    // Spiegelen met een negatieve schaal: three.js draait dan zelf de
    // voorkant van de driehoeken om, dus de belichting blijft kloppen.
    if (spiegel.current) spiegel.current.scale.x = (kant?.current ?? 1) < 0 ? -1 : 1
  })

  return (
    <group scale={SCHAAL}>
      <Chassis m={m} />
      <Aandrijfkop m={m} />
      <Mast m={m} binnen={binnen} />
      <Slanggeleiders m={m} />
      <group ref={hefGroep}>
        <Cabine m={m} />
        <group ref={spiegel}>
          <Draaikop m={m} schuif={schuif} lading={lading} />
        </group>
      </group>
    </group>
  )
}
